import { z } from "zod";
import { createJsonStorage } from "@/lib/storage/create-json-storage";

export const THEME_STORAGE_KEY: string = "timeghost-theme";
export const THEME_CHANGE_EVENT: string = "timeghost-theme-change";

const THEME_CLASS_NAMES: Record<ResolvedTheme, string> = {
	light: "light",
	dark: "dark",
};
const SYSTEM_THEME_QUERY: string = "(prefers-color-scheme: dark)";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";
/** `user.settings.theme`. `default` follows the system. */
export type UserThemeSetting = "default" | "dark" | "light";

export interface ThemeChangeDetail {
	readonly preference: ThemePreference;
	readonly resolvedTheme: ResolvedTheme;
}

interface ViewTransitionController {
	readonly finished: Promise<void>;
}

interface DocumentWithOptionalViewTransition {
	readonly startViewTransition?: (updateCallback: () => void | Promise<void>) => ViewTransitionController;
}

const themeStorage = createJsonStorage({
	key: THEME_STORAGE_KEY,
	schema: z.enum(["light", "dark", "system"]),
	fallback: "system",
});

const getMediaQueryList = (): MediaQueryList => window.matchMedia(SYSTEM_THEME_QUERY);

export const getSystemTheme = (): ResolvedTheme => {
	if (typeof window === "undefined") {
		return "light";
	}

	return getMediaQueryList().matches ? "dark" : "light";
};

export const getStoredThemePreference = (): ThemePreference => themeStorage.get();

export const resolveThemePreference = (preference: ThemePreference): ResolvedTheme =>
	preference === "system" ? getSystemTheme() : preference;

export const themePreferenceFromUserSetting = (value: unknown): ThemePreference =>
	value === "light" || value === "dark" ? value : "system";

export const userThemeSettingFromPreference = (preference: ThemePreference): UserThemeSetting =>
	preference === "system" ? "default" : preference;

type ThemeSaver = (preference: ThemePreference) => void;

let themeSaver: ThemeSaver | null = null;

/** App session registers the user-settings write. The paint cache stays local. */
export const bindThemePersistence = (save: ThemeSaver): (() => void) => {
	themeSaver = save;
	return () => {
		if (themeSaver === save) {
			themeSaver = null;
		}
	};
};

const applyResolvedTheme = (resolvedTheme: ResolvedTheme): void => {
	const rootElement: HTMLElement = document.documentElement;
	rootElement.classList.remove("light", "dark");
	rootElement.classList.add(THEME_CLASS_NAMES[resolvedTheme]);
	rootElement.dataset.theme = resolvedTheme;
	rootElement.style.colorScheme = resolvedTheme;
};

const emitThemeChange = (preference: ThemePreference, resolvedTheme: ResolvedTheme): void => {
	const detail: ThemeChangeDetail = { preference, resolvedTheme };
	window.dispatchEvent(new CustomEvent<ThemeChangeDetail>(THEME_CHANGE_EVENT, { detail }));
};

const applyThemePreference = (
	preference: ThemePreference,
	options?: {
		readonly persist?: boolean;
		readonly notify?: boolean;
		readonly save?: boolean;
	}
): void => {
	const persist: boolean = options?.persist ?? true;
	const notify: boolean = options?.notify ?? true;
	const save: boolean = options?.save ?? false;
	const resolvedTheme: ResolvedTheme = resolveThemePreference(preference);

	applyResolvedTheme(resolvedTheme);

	if (persist) {
		themeStorage.set(preference);
	}

	if (notify) {
		emitThemeChange(preference, resolvedTheme);
	}

	if (save) {
		themeSaver?.(preference);
	}
};

/** Apply a stored user theme to the document and the paint cache. Does not write user settings. */
export const applyCachedThemePreference = (preference: ThemePreference): void => {
	applyThemePreference(preference);
};

export const initializeTheme = (): ThemePreference => {
	if (typeof window === "undefined") {
		return "system";
	}

	const preference: ThemePreference = getStoredThemePreference();
	applyThemePreference(preference, { persist: false, notify: false });

	return preference;
};

export const setThemePreference = (
	preference: ThemePreference,
	options?: {
		readonly useViewTransition?: boolean;
	}
): void => {
	if (typeof document === "undefined" || typeof window === "undefined") {
		return;
	}

	const runThemeUpdate = (): void => {
		applyThemePreference(preference, { save: true });
	};

	if (options?.useViewTransition !== true) {
		runThemeUpdate();
		return;
	}

	const documentWithViewTransition: DocumentWithOptionalViewTransition = document as DocumentWithOptionalViewTransition;

	if (typeof documentWithViewTransition.startViewTransition !== "function") {
		runThemeUpdate();
		return;
	}

	void documentWithViewTransition.startViewTransition(() => {
		runThemeUpdate();
	}).finished;
};

let isSystemThemeListenerActive = false;

export const startSystemThemeListener = (): void => {
	if (typeof window === "undefined" || isSystemThemeListenerActive) {
		return;
	}

	const mediaQueryList: MediaQueryList = getMediaQueryList();

	const handleSystemThemeChange = (): void => {
		const preference: ThemePreference = getStoredThemePreference();

		if (preference !== "system") {
			return;
		}

		applyThemePreference("system", { persist: false, notify: true });
	};

	mediaQueryList.addEventListener("change", handleSystemThemeChange);
	isSystemThemeListenerActive = true;
};
